import teamClient from '@/api/teams.js'
import { setSentryUserContext } from '@/utils/log.js'
import { logShowSignupForm } from '@/utils/segment.js'
import { AUTH_PAGES_SWITCH, goToAuthPage } from '@/utils/authPages.js'

// initial state
const state = {
  loadingMe: false,
  currentUser: null,
  currentTeam: null,
  currentMembership: null,
  showSignupModal: false
}

// getters
const getters = {
  currentUser: (state) => state.currentUser,

  currentTeam: (state) => state.currentTeam,

  currentMembership: (state) => state.currentMembership,

  loadingMe: (state) => state.loadingMe,

  fullName: (state) => {
    if (state.currentUser !== null) {
      return (state.currentUser.first_name + ' ' + state.currentUser.last_name).trim()
    }
  },

  showSignupModal: (state) => state.showSignupModal
}

// actions
const actions = {
  setMe: (context) => {
    context.commit(SET_LOADING_ME, true)
    return teamClient.me()
      .then((response) => {
        const user = response.data
        const membership = user.membership
        const team = membership.team

        delete user.membership
        delete membership.team

        context.commit(SET_CURRENT_USER, user)
        context.commit(SET_CURRENT_TEAM, team)
        context.commit(SET_CURRENT_MEMBERSHIP, membership)
        context.commit(SET_LOADING_ME, false)

        context.dispatch('subscriptions/setCreateSubscriptionWithTrial', team.has_trials_remaining, { root: true })

        setSentryUserContext(user)
      })
      .catch(error => {
        if (error.response.status === 401) {
          context.dispatch('auth/logout', null, { root: true })
          context.commit(SET_LOADING_ME, false)
        }
      })
  },

  clearCurrentObjects: (context) => {
    context.commit(SET_CURRENT_USER, null)
    context.commit(SET_CURRENT_TEAM, null)
    context.commit(SET_CURRENT_MEMBERSHIP, null)
  },

  // prompt ({ reason, track }) is the track action that needs an account, e.g.
  // { reason: 'favourite', track }. With the switch on it shows the track first
  // (TheTrackPromptModal), whose button comes back here without one.
  showSignupModal: (context, prompt) => {
    // Wait for the switch so an early click doesn't open the modal it replaces.
    // If waffle can't load, fall back to the modal.
    return context.dispatch('waffle/ensureLoaded', null, { root: true })
      .catch(() => {})
      .then(() => {
        if (context.rootGetters['waffle/isFeatureEnabled'](AUTH_PAGES_SWITCH)) {
          if (prompt) {
            return context.dispatch('common/showTrackPrompt', prompt, { root: true })
          }
          // The page takes the destination as ?next=, so don't leave it in the store too.
          const redirectTo = context.rootGetters['auth/redirectTo']
          context.dispatch('auth/clearRedirectTo', null, { root: true })
          // Imported here because the router imports the store. The page logs the
          // form being shown.
          return import('@/router.js').then(({ default: router }) => goToAuthPage(router, 'create-account', redirectTo))
        }
        context.commit(SET_SHOW_SIGNUP_MODAL, true)
        logShowSignupForm()
      })
  },

  hideSignupModal: (context) => {
    context.commit(SET_SHOW_SIGNUP_MODAL, false)
  }
}

// mutations
const SET_LOADING_ME = 'SET_LOADING_ME'
const SET_CURRENT_USER = 'SET_CURRENT_USER'
const SET_CURRENT_TEAM = 'SET_CURRENT_TEAM'
const SET_CURRENT_MEMBERSHIP = 'SET_CURRENT_MEMBERSHIP'
const SET_SHOW_SIGNUP_MODAL = 'SET_SHOW_SIGNUP_MODAL'

const mutations = {
  [SET_LOADING_ME]: (state, value) => {
    state.loadingMe = value
  },

  [SET_CURRENT_USER]: (state, payload) => {
    state.currentUser = payload
  },

  [SET_CURRENT_TEAM]: (state, payload) => {
    state.currentTeam = payload
  },

  [SET_CURRENT_MEMBERSHIP]: (state, payload) => {
    state.currentMembership = payload
  },

  [SET_SHOW_SIGNUP_MODAL]: (state, payload) => {
    state.showSignupModal = payload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
